/**
 * Member Milestone Progress Selector
 * Allows user to select a program and member, then displays milestone progress
 * Trust Marketplace - Configurable Milestone System
 */

import React, { useState, useEffect } from 'react';
import { Card } from '../ui/card';
import { LoadingSpinner } from '../ui/LoadingSpinner';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../ui/select';
import { Label } from '../ui/label';
import { getWeaveinApiUrl } from '@/src/config';
import MemberMilestoneProgress from './MemberMilestoneProgress';
import { AlertCircle } from 'lucide-react';

import { useAppTranslation } from '@/contexts/TranslationContext';
interface Program {
  id: number;
  name: string;
}

interface Member {
  id: number;
  first_name: string;
  last_name: string;
}

const MemberMilestoneProgressSelector: React.FC = () => {
  const { t } = useAppTranslation();
  const [programs, setPrograms] = useState<Program[]>([]);
  const [members, setMembers] = useState<Member[]>([]);
  const [selectedProgramId, setSelectedProgramId] = useState<number | null>(null);
  const [selectedMemberId, setSelectedMemberId] = useState<number | null>(null);
  const [loading, setLoading] = useState(true);
  const [loadingMembers, setLoadingMembers] = useState(false);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    fetchPrograms();
  }, []);

  useEffect(() => {
    if (selectedProgramId) {
      fetchProgramMembers();
    }
  }, [selectedProgramId]);

  const fetchPrograms = async () => {
    try {
      setLoading(true);
      const cboId = localStorage.getItem('active_cbo_id');
      const url = getWeaveinApiUrl(`/api/programs?cbo_id=${cboId}`);
      const token = localStorage.getItem('auth_token');

      const response = await fetch(url, {
        headers: {
          'Authorization': `Bearer ${token}`,
          'X-CBO-ID': cboId || ''
        }
      });

      if (!response.ok) {
        throw new Error('Failed to fetch programs');
      }

      const data = await response.json();
      setPrograms(data);

      // Auto-select first program
      if (data.length > 0) {
        setSelectedProgramId(data[0].id);
      }
    } catch (err) {
      console.error('Error fetching programs:', err);
      setError(err instanceof Error ? err.message : 'Failed to load programs');
    } finally {
      setLoading(false);
    }
  };

  const fetchProgramMembers = async () => {
    if (!selectedProgramId) return;

    try {
      setLoadingMembers(true);
      const cboId = localStorage.getItem('active_cbo_id');
      const url = getWeaveinApiUrl(`/api/programs/${selectedProgramId}/enrollments`);
      const token = localStorage.getItem('auth_token');

      const response = await fetch(url, {
        headers: {
          'Authorization': `Bearer ${token}`,
          'X-CBO-ID': cboId || ''
        }
      });

      if (!response.ok) {
        throw new Error('Failed to fetch program members');
      }

      const data = await response.json();

      // Extract unique members
      const uniqueMembers = Array.from(
        new Map(data.map((enrollment: any) => [enrollment.member_id, {
          id: enrollment.member_id,
          first_name: enrollment.member_first_name || 'Unknown',
          last_name: enrollment.member_last_name || ''
        }])).values()
      );

      setMembers(uniqueMembers as Member[]);

      // Auto-select first member
      if (uniqueMembers.length > 0) {
        setSelectedMemberId(uniqueMembers[0].id);
      } else {
        setSelectedMemberId(null);
      }
    } catch (err) {
      console.error('Error fetching members:', err);
      setMembers([]);
      setSelectedMemberId(null);
    } finally {
      setLoadingMembers(false);
    }
  };

  if (loading) {
    return (
      <div className="flex justify-center items-center h-64">
        <LoadingSpinner />
      </div>
    );
  }

  if (error) {
    return (
      <Card className="p-6 bg-red-50 border-red-200">
        <div className="flex items-center gap-2 text-red-800">
          <AlertCircle className="w-5 h-5" />
          <span className="font-medium">Error: {error}</span>
        </div>
      </Card>
    );
  }

  if (programs.length === 0) {
    return (
      <Card className="p-12 text-center">
        <AlertCircle className="w-12 h-12 text-gray-300 mx-auto mb-4" />
        <h3 className="text-lg font-semibold text-gray-900 mb-2">{t('incentives.no_programs_found', 'No Programs Found')}</h3>
        <p className="text-sm text-gray-600">
          {t('incentives.create_a_program_with_milestones', 'Create a program with milestones first')}
        </p>
      </Card>
    );
  }

  return (
    <div className="space-y-6">
      {/* Selectors */}
      <Card className="p-6">
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          <div>
            <Label htmlFor="program_select">{t('incentives.select_program', 'Select Program')}</Label>
            <Select
              value={selectedProgramId?.toString() || ''}
              onValueChange={(value) => setSelectedProgramId(parseInt(value))}
            >
              <SelectTrigger id="program_select">
                <SelectValue placeholder={t('incentives.choose_a_program', 'Choose a program')} />
              </SelectTrigger>
              <SelectContent>
                {programs.map((program) => (
                  <SelectItem key={program.id} value={program.id.toString()}>
                    {program.name}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>

          <div>
            <Label htmlFor="member_select">{t('incentives.select_member', 'Select Member')}</Label>
            <Select
              value={selectedMemberId?.toString() || ''}
              onValueChange={(value) => setSelectedMemberId(parseInt(value))}
              disabled={loadingMembers || members.length === 0}
            >
              <SelectTrigger id="member_select">
                <SelectValue placeholder={loadingMembers ? "Loading members..." : "Choose a member"} />
              </SelectTrigger>
              <SelectContent>
                {members.map((member) => (
                  <SelectItem key={member.id} value={member.id.toString()}>
                    {member.first_name} {member.last_name}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>
        </div>

        {selectedProgramId && members.length === 0 && !loadingMembers && (
          <p className="text-sm text-yellow-600 mt-2">
            {t('incentives.no_members_enrolled_in_this', 'No members enrolled in this program yet')}
          </p>
        )}
      </Card>

      {/* Progress Dashboard */}
      {selectedProgramId && selectedMemberId && (
        <MemberMilestoneProgress programId={selectedProgramId} memberId={selectedMemberId} />
      )}
    </div>
  );
};

export default MemberMilestoneProgressSelector;
